import { assert_nested_coords, deep_copy_coords } from "../MapViewController";
import { SkDisplayItemBase } from "../stek/sk-display";

const bClassRegistry = {};

const kFloorZBias = 20000.0;
const kOnFloorItemZBias = 2000.0;
const kAbortEnum = -1;
const BEV_REQUEST_EXECUTE = Symbol("BEV_REQUEST_EXECUTE");
const BEV_REQUEST_UPDATE_UNDO = Symbol("BEV_REQUEST_UPDATE_UNDO");
const BEV_MODIFIED = Symbol("BEV_MODIFIED");
const BEV_REMOVED = Symbol("BEV_REMOVED");
const BEV_SHAPE_MAJOR_CHANGE = Symbol("BEV_SHAPE_MAJOR_CHANGE");
const BEV_FLOOR_ADDED = Symbol("BEV_FLOOR_ADDED");
var autoId = 1;

const B_AttrFlags = {
	BuildingOuter: 0x01,
	NoBend: 0x02,
	OpenRing: 0x04,
	ElevatorNode: 0x10,
	Stairs: 0x20
};

// Auto ID API
function fetchAutoId() { return autoId++; }
function getCurrentAutoId() { return autoId; }
function forceSetAutoId(i) { autoId = i; };

class BBase {
	constructor(flags, forceSetId) {
		this.flags = flags ?? 0;

		this._eventListeners = [];
		this._eventParent = null;

		let newId = forceSetId;
		if (!Number.isSafeInteger(newId)) {
			newId = fetchAutoId();
		}

		this.id = newId;

		this.representativeDisplayItem = null;
	}

	setEventParent(pr) {
		this._eventParent = pr;
		pr.onAddToTree?.(this);
	}

	getParentDisplayItemIf() {
		if (this._eventParent) {
			return this._eventParent.representativeDisplayItem;
		}

		return null;
	}

	useStandardDisplayItemAsRepresentative() {
		if (!this.representativeDisplayItem) {
			this.representativeDisplayItem = new SkDisplayItemBase();
			this.representativeDisplayItem.useLocalTransform();
		}
	}

	addEventListener(f) {
		this._eventListeners.push(f);
	}

	fireEvent(sender, type, param) {
		for (const listener of this._eventListeners) {
			listener(sender, type, param);
		}

		this._eventParent?.fireEvent(sender, type, param);
	}

	requestCommandExec(cmd) {
		this.fireEvent(this, BEV_REQUEST_EXECUTE, cmd);
	}

	requestUpdateUndo(cmd) {
		this.fireEvent(this, BEV_REQUEST_UPDATE_UNDO, cmd);
	}

	generateDisplayList(outList) {

	}

	addRepresentativeDisplayItem(outList) {
		const rep = this.representativeDisplayItem;
		if (rep) {
			rep.clearTransformChildren();
			outList.push(rep);
		}

		return rep;
	}

	connectTransformTree(outList) {
		const selfRep = this.addRepresentativeDisplayItem(outList);
		const pr = this.getParentDisplayItemIf();
		if (selfRep && pr) {
			pr.addTransformChild(selfRep);
		}

		return selfRep;
	}

	connectTransformTreeItemIf(childDisplayItem) {
		this.representativeDisplayItem?.addTransformChild(childDisplayItem);
	}

	getOwnerBuilding() {
		if (this.isBuilding) { return this; }
		return this._eventParent?.getOwnerBuilding();
	}

	dispose() {
		this.willDispose(this);
	}

	willDispose(item) {
		this._eventParent?.willDispose?.(item);
	}

	getParent() {
		return this._eventParent;
	}

	serialize() {
		const o = {
			id: this.id,
			flags: this.flags,
			className: this.constructor.name
		};

		const pr = this.getParent();
		if (pr) {
			o._parent_id = pr.id;
			if (pr.getNextItem) {
				o._next_id = pr.getNextItem(this)?.id ?? null;
			}
		}

		return o;
	}

	deserialize(_o) {
		throw new Error("not implemented");
	}

	static createDeserializedInstance(o) {
		const klass = bClassRegistry[o.className];
		return new klass(o.flags, o.id);
	}
}

class BContainer extends BBase {
	constructor(flags, forceSetId) {
		super(flags, forceSetId);
		this.childItems = [];
	}

	get length() {
		return this.childItems.length;
	}

	addItem(item, beforeThisId) {
		const beforeThisIndex = this.findIndexById(beforeThisId);
		push_or_insert(this.childItems, item, beforeThisIndex)

		item.setEventParent(this);
	}

	findIndexById(id) {
		const ls = this.childItems;
		const n = ls.length;
		for (let i = 0;i < n;++i) {
			if (ls[i].id === id) { return i; }
		}

		return -1;
	}

	getNextItem(item) {
		const i = this.childItems.indexOf(item);
		if (i >= (this.childItems.length-1) ) { return null; }

		return this.childItems[i + 1];
	}

	removeItem(item) {
		const i = this.childItems.indexOf(item);
		if (i >= 0) {
			item.dispose();
			this.childItems.splice(i, 1);
		}

		this.afterItemRemove?.();
		this.fireEvent(this, BEV_REMOVED);
		return i;
	}

	forEach(fn) {
		const ls = this.childItems;
		const n = ls.length;
		for (let i = 0;i < n;++i) {
			const res = fn(ls[i], i, ls);
			if (res === kAbortEnum) { break; }
		}
	}

	onAddToTree(newItem) {
		this._eventParent?.onAddToTree?.(newItem);
	}

	dispose() {
		this.childItems.forEach( ch => ch.dispose() );
		super.dispose();
	}

	serializeChildren() {
		return this.childItems.map( c => c.serialize() );
	}

	serialize() {
		const o = super.serialize();
		o.children = this.serializeChildren();
		return o;
	}
}

function push_or_insert(arr, item, beforeThisIndex) {
	if (Number.isFinite(beforeThisIndex) && beforeThisIndex >= 0) {
		arr.splice(beforeThisIndex, 0, item);
	} else {
		arr.push(item);
	}
}

const addFloorGetSet = target => class extends target {
	getFloor() {
		return this.floor;
	}

	setFloor(f) {
		this.floor = f;
	}
};

const addCoordinatesOperation = target => class extends target {
	getLengthOfLoop(i) {
		return this.coordinates[i].length;
	}

	countLoops() {
		return this.coordinates.length;
	}

	addLoop(loopCoords, beforeThisIndex) {
		push_or_insert( this.coordinates, deep_copy_coords(loopCoords), beforeThisIndex);
		this.updateXyCoordinates();
	}

	removeLoop(index) {
		this.coordinates.splice(index, 1);
		this.updateXyCoordinates();
	}

	getPrevCoordinateAt(loopIndex, index) {
		return this.prevCoordinates[loopIndex][index];
	}

	getCoordinateAt(loopIndex, index) {
		return this.coordinates[loopIndex][index];
	}

	saveCoordinates() {
		this.prevCoordinates = deep_copy_coords(this.coordinates);
	}

	modifyCoordinate(loopIndex, vIndex, newX, newY, userData) {
		if (this.coordinates[loopIndex]?.[vIndex]) {
			this.coordinates[loopIndex][vIndex][0] = newY;
			this.coordinates[loopIndex][vIndex][1] = newX;
		}

		this.updateXyCoordinates();
		this.fireCoodinateChange(loopIndex, vIndex, userData);
	}

	#addCoordinate(loopIndex, c) {
		this.coordinates[loopIndex].push(c);
		return this;
	}

	clearLoop(loopIndex) {
		this.coordinates[loopIndex].length = 0;
		this.xyCoordinates[loopIndex].length = 0;
		return this;		
	}

	copyAllCoordinates(src) {
		this.coordinates = deep_copy_coords(src);
	}

	addCoordinatesFromArray(loopIndex, a) {
		a.forEach( c => this.#addCoordinate(loopIndex, Array.from(c) ) ); // deep copy
		this.updateXyCoordinates();

		return this;
	}

	updateXyCoordinates() {
		yxToXy(this.xyCoordinates, this.coordinates);
	}

	fireCoodinateChange(loopIndex, vIndex, userData) {
		if (this.onCoodinateChange) {
			this.onCoodinateChange(this, loopIndex, vIndex, userData);
			this.fireEvent(this, BEV_MODIFIED);
		}
	}

	fireLengthChange() {
		if (this.onLengthChange) {
			this.onLengthChange(this);
		}
	}
};

function addFloorObjProps(target) {
	target.floor = null;
	
	target.coordinates = []; // Lat,Lng order
	target.prevCoordinates = [];

	target.xyCoordinates = []; // X,Y order		

	target.onCoodinateChange = null;
	target.onLengthChange = null;
}

function register_b_class(klass) {
	bClassRegistry[klass.name] = klass;
}

function serialize_mat4(m) {
	const a = new Float64Array(16);
	m.exportGL(a);
	return a;
}

function yxToXy(destArray, srcArray) {
	assert_nested_coords(srcArray);
	destArray.length = 0;

	const nLoops = srcArray.length;
	prepareMultiLoops(destArray, nLoops);
	for (let i = 0;i < nLoops;++i) {
		push_loop_coords_rev(destArray[i], srcArray[i]);
	}

	return destArray;
}

function prepareMultiLoops(outArray, n) {
	for (let i = 0;i < n;++i) {
		outArray.push([]);
	}
}

function push_loop_coords_rev(outArray, srcLoop) {
	for (const cd of srcLoop) {
		outArray.push([cd[1], cd[0]]);
	}

	return outArray;
}

export default BContainer;
export { BBase, fetchAutoId, forceSetAutoId, getCurrentAutoId, register_b_class, serialize_mat4, addFloorGetSet, addFloorObjProps, addCoordinatesOperation,
	kFloorZBias, kOnFloorItemZBias,
	push_or_insert, BEV_MODIFIED, BEV_REMOVED, BEV_FLOOR_ADDED, BEV_SHAPE_MAJOR_CHANGE, BEV_REQUEST_UPDATE_UNDO, BEV_REQUEST_EXECUTE, kAbortEnum, B_AttrFlags } ;